import { useState, useRef, useEffect, useCallback } from 'react';
import { StarIcon } from '@heroicons/react/24/solid';
import { ChatBubbleBottomCenterTextIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useContent } from '../context/ContentContext';
import type { TestimonialItem } from '../types/cms';

function useIsTruncated(clampClass: string) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setIsTruncated(el.scrollHeight > el.clientHeight + 1);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [clampClass]);

  return { ref, isTruncated };
}

function ReviewLightbox({
  testimonial,
  onClose,
}: {
  testimonial: TestimonialItem;
  onClose: () => void;
}) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-2xl p-6 sm:p-8 max-w-lg w-full shadow-2xl animate-lightbox-in"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100 transition-colors"
          aria-label="Close review"
        >
          <XMarkIcon className="w-5 h-5 text-gray-400" />
        </button>

        <div className="flex mb-4" style={{ gap: '0px' }}>
          {[...Array(testimonial.rating)].map((_, i) => (
            <StarIcon key={i} className="w-5 h-5 text-brand-orange" />
          ))}
        </div>

        <p className="text-brand-warm-gray leading-relaxed mb-6">
          &quot;{testimonial.text}&quot;
        </p>

        <div className="border-t border-gray-100 pt-4">
          <p className="font-semibold text-brand-navy">{testimonial.name}</p>
          <p className="text-sm text-brand-warm-gray">{testimonial.location}</p>
        </div>
      </div>
    </div>
  );
}

function DesktopScrollCard({
  testimonial,
  onOpen,
}: {
  testimonial: TestimonialItem;
  onOpen: () => void;
}) {
  const { ref, isTruncated } = useIsTruncated('line-clamp-4');

  return (
    <div
      className={`bg-white rounded-2xl p-8 shadow-sm relative flex-shrink-0 w-[400px] transition-shadow duration-200 ${
        isTruncated ? 'cursor-pointer hover:shadow-md' : ''
      }`}
      onClick={isTruncated ? onOpen : undefined}
      role={isTruncated ? 'button' : undefined}
      tabIndex={isTruncated ? 0 : undefined}
      aria-label={isTruncated ? `Read the full review from ${testimonial.name}` : undefined}
      onKeyDown={isTruncated ? event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onOpen(); } } : undefined}
    >
      <ChatBubbleBottomCenterTextIcon className="absolute top-6 right-6 w-10 h-10 text-brand-orange/10" />
      <div className="flex mb-4" style={{ gap: '0px' }}>
        {[...Array(testimonial.rating)].map((_, i) => (
          <StarIcon key={i} className="w-5 h-5 text-brand-orange" />
        ))}
      </div>
      <p ref={ref} className="text-brand-warm-gray mb-6 leading-relaxed line-clamp-4">&quot;{testimonial.text}&quot;</p>
      {isTruncated && (
        <span className="text-brand-orange text-sm font-medium">Read more</span>
      )}
      <div className="border-t border-gray-100 pt-4">
        <p className="font-semibold text-brand-navy">{testimonial.name}</p>
        <p className="text-sm text-brand-warm-gray">{testimonial.location}</p>
      </div>
    </div>
  );
}

function MobileStackedCard({
  testimonial,
  onOpen,
}: {
  testimonial: TestimonialItem;
  onOpen: () => void;
}) {
  const { ref, isTruncated } = useIsTruncated('line-clamp-3');

  return (
    <div
      className={`bg-white rounded-xl p-5 shadow-sm relative ${
        isTruncated ? 'cursor-pointer active:bg-gray-50' : ''
      }`}
      onClick={isTruncated ? onOpen : undefined}
      role={isTruncated ? 'button' : undefined}
      tabIndex={isTruncated ? 0 : undefined}
      aria-label={isTruncated ? `Read the full review from ${testimonial.name}` : undefined}
      onKeyDown={isTruncated ? event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onOpen(); } } : undefined}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="font-semibold text-brand-navy text-[15px]">{testimonial.name}</p>
          <p className="text-xs text-brand-warm-gray">{testimonial.location}</p>
        </div>
        <div className="flex" style={{ gap: '0px' }}>
          {[...Array(testimonial.rating)].map((_, i) => (
            <StarIcon key={i} className="w-4 h-4 text-brand-orange" />
          ))}
        </div>
      </div>
      <p ref={ref} className="text-brand-warm-gray text-sm leading-relaxed line-clamp-3">&quot;{testimonial.text}&quot;</p>
      {isTruncated && (
        <span className="text-brand-orange text-xs font-medium mt-1 inline-block">Read more</span>
      )}
    </div>
  );
}

const INITIAL_MOBILE_COUNT = 4;

export default function Testimonials() {
  const { content, settings } = useContent();
  const t = content.testimonials;
  const testimonials = t.testimonials;

  const [showAll, setShowAll] = useState(false);
  const [activeReview, setActiveReview] = useState<TestimonialItem | null>(null);
  const closeLightbox = useCallback(() => setActiveReview(null), []);
  const topRow = testimonials.slice(0, 4);
  const bottomRow = testimonials.slice(4, 8);
  const mobileVisible = showAll ? testimonials : testimonials.slice(0, INITIAL_MOBILE_COUNT);

  return (
    <section id="testimonials" className="py-16 sm:py-20 lg:py-24 bg-brand-off-white relative overflow-hidden">
      <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-8 sm:mb-16">
          <div className="inline-flex items-center gap-2 border border-brand-orange/30 rounded-lg px-4 py-2 mb-6">
            <span className="w-2 h-2 bg-brand-orange rounded-full" />
            <span className="text-brand-orange font-semibold text-sm uppercase tracking-wider">
              {t.sectionLabel}
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-brand-navy mb-4 sm:mb-6">
            {t.heading}
          </h2>
          <p className="text-[#756c65] text-sm sm:text-lg max-w-2xl mx-auto">
            {t.description}
          </p>
        </div>
      </div>

      <div className="lg:hidden max-w-site mx-auto px-4 sm:px-6">
        <div className="space-y-3">
          {mobileVisible.map((testimonial, index) => (
            <MobileStackedCard key={index} testimonial={testimonial} onOpen={() => setActiveReview(testimonial)} />
          ))}
        </div>

        {!showAll && testimonials.length > INITIAL_MOBILE_COUNT && (
          <div className="text-center mt-6">
            <button
              onClick={() => setShowAll(true)}
              className="inline-flex items-center gap-2 bg-brand-navy text-white px-6 py-3 rounded-lg font-semibold text-sm hover:bg-brand-orange transition-colors duration-300"
            >
              Show More Reviews ({testimonials.length - INITIAL_MOBILE_COUNT} more)
            </button>
          </div>
        )}

        {showAll && (
          <div className="text-center mt-6">
            <button
              onClick={() => setShowAll(false)}
              className="inline-flex items-center gap-2 text-brand-navy font-semibold text-sm hover:text-brand-orange transition-colors"
            >
              Show Less
            </button>
          </div>
        )}
      </div>

      <div className="hidden lg:block space-y-6">
        <div className="relative overflow-hidden">
          <div className="flex gap-6 animate-scroll-left">
            {[...topRow, ...topRow, ...topRow].map((testimonial, index) => (
              <DesktopScrollCard key={index} testimonial={testimonial} onOpen={() => setActiveReview(testimonial)} />
            ))}
          </div>
        </div>

        <div className="relative overflow-hidden">
          <div className="flex gap-6 animate-scroll-right">
            {[...bottomRow, ...bottomRow, ...bottomRow].map((testimonial, index) => (
              <DesktopScrollCard key={index} testimonial={testimonial} onOpen={() => setActiveReview(testimonial)} />
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mt-8 sm:mt-12">
          <a
            href={settings.googleReviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-brand-navy font-semibold hover:text-brand-orange transition-colors text-sm sm:text-base"
          >
            <div className="flex" style={{ gap: '0px' }}>
              {[...Array(5)].map((_, i) => (
                <StarIcon key={i} className="w-4 h-4 sm:w-5 sm:h-5 text-brand-orange" />
              ))}
            </div>
            <span>{t.googleLinkText}</span>
          </a>
        </div>
      </div>

      {activeReview && (
        <ReviewLightbox testimonial={activeReview} onClose={closeLightbox} />
      )}
    </section>
  );
}
